// utils/analytics/capabilities.js
//
// WHAT A CHART MAY ASK OF EACH DATA TYPE — the capability table of the charts
// program (keepr-web docs/plans/analytics.md §4.2, ruled 2026-09-29). It is
// the "intelligence" the owner asked for ("don't try to sum text fields"):
// the builder offers exactly what this table allows, and the server refuses
// the rest by name (utils/analytics/spec.js), so the two can never disagree.
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. This file is vendored
// byte-identical into keepr-web (`src/utils/analytics/vendored/`) with the
// vector file beside it (`vectors.json`), which both repos' suites drive, and
// `test/vendoredParity.test.js` compares the two mains. Nothing here may
// require() anything, and there is NO SPREAD SYNTAX: Babel would inject a
// helper import for it, turning this CommonJS file into an ES module with no
// default export in the web build (knowledge/history/calculated-elements.md).
// Where the table depends on another registry — which measures are affine or
// counts, which names KQL reserves — it names them as data, and a keepr-api
// test holds that data to the registry (test/analyticsCapabilities.test.js).
//
// The table, in one breath (§4.2):
//
//   number · decimal · integer   count, distinct, total, average, median,
//                                smallest, largest, latest · ranges · Total by month.
//                                Auto-numbering (options.sequence): count and
//                                distinct only. A number stored as text is left
//                                out of ranges, as KQL leaves it out.
//   currency                     total, average, median, smallest, largest, per
//                                currency; never across codes · ranges per currency.
//   measurement                  total, average, median, smallest, largest, latest,
//                                on `base` · ranges. Temperature and fuel economy
//                                never total; count units total within one unit.
//   rating                       average, median, smallest, largest, latest; never
//                                totaled · each rating value (a distribution).
//   confirmations                total, average, median, smallest, largest · ranges.
//                                A blank IS zero (nobody confirmed), so it is
//                                never "Not set" and "Count as zero" is moot.
//   boolean                      count of Yes, share of Yes · Yes, No, Not set.
//   choice                       count, distinct · each choice in list order, in its
//                                own colour. `allowMultiple` is the multi-choice slot.
//                                A list on any other type is inert (KPE-033):
//                                a short text carrying one charts as text.
//   item lookup · user           distinct · each item / user, top 10.
//   date · date-time             earliest, latest, count · hour (date-time), day,
//                                week, month, quarter, year, floored by `precision`;
//                                day of week, hour of day (date-time) and month of
//                                year — the cyclic parts, whose doors are KQL's date
//                                parts (q20, PR AK: `due.weekday = mon`).
//                                Several dates: earliest, latest or each (d06).
//   time                         earliest, latest · hour of day, 30-minute slots.
//   text-small · email           count non-blank, distinct · top values + Other.
//   url · phone                  count non-blank, distinct.
//   text-large · rich-text ·     count non-blank.
//   location
//   created · updated (system)   earliest, latest · as a date-time; the default
//                                time axis.
//   card · collection (system)   each card / each collection in the family.
//   created.by (system)          who added an item, top 10 (its door: KQL
//                                `created.by = <id>`, PR AK).
//   tags                         not in v1.
//   repeating group (KPR-684)    not measured or grouped itself: a chart COUNTS
//                                its entries (the spec's `entries`) and reads
//                                its columns (describeColumnForCharts below).
//   a group's column             counting entries: its type's ops, `latest`
//                                only while the chart's date is one of the
//                                group's own dates (KPR-806: else an item's
//                                entries share its date), its type's groups,
//                                a date's cyclic parts included (KPR-935: KQL
//                                reads `given.weekday` inside a block, so a
//                                bar's door is `g has (given.weekday = mon)`)
//                                but on a column holding several dates;
//                                a date or date-time column holding one date
//                                is the chart's date (each entry placed by its
//                                own, KPR-806). Counting items: grouped only,
//                                each item once in each value its entries
//                                hold, never the chart's date.
//
// The second half says which chart FORMS an answer shape may take
// (formsForShape), which DEFAULT chart picking an element proposes
// (defaultChartFor), whether a group CAN open an exact list — a door
// (doorFor, §5.4 and d28), and which KQL compile family an element falls in,
// the one thing the collision rule compares (kqlFamilyOf, d37).

'use strict';

// ---------------------------------------------------------------- the limits

// The charts program's limits that shape what may be ASKED (§4.7, d12). The
// run-time limits (items read, batch size, deadlines) belong to the engine.
const LIMITS = Object.freeze({
	plainMeasures: 4,
	formulaMeasures: 2,
	// Split values drawn at once, Other and Not set included; the rest fold
	// into "Other". The series cap FOLLOWS THE PALETTE (plan §6.8): it must
	// equal keepr-web's DATA_SERIES (src/config/themes.js), the categorical
	// colours that pass every theme × mode check — six. A cross-repo test
	// holds the two together once both are on main (A4).
	series: 6,
	categories: 50,     // on an axis; the rest fold into "Other"
	topValues: 25,      // free text and email: the most common values
	defaultTop: 10,     // lookups, users and top values, until the author says otherwise
	buckets: 400,       // time buckets or bins on an axis
	heatmapAxis: 60,    // each axis of a heat map
	heatmapCells: 2000,
	donutParts: 6,      // top 5 + Other
	labelLength: 80,
	goalAbs: 1e15,
});

// The ranges a group by a number, an amount or a measurement proposes when
// picked (defaultChartFor): equal ranges, this many.
const DEFAULT_BINS = 10;

// ------------------------------------------------------------------- the ops

const OPS = ['count', 'countNonBlank', 'distinct', 'sum', 'avg', 'median', 'min', 'max', 'latest', 'countYes', 'shareYes'];

// `count` counts ITEMS and takes no element; every other op measures one.
const ITEM_OPS = ['count'];

// What an op answers: a number of items, a share (0..1), or a value of the
// measured element's own kind (money stays money, a date stays a date).
const OP_ANSWERS = Object.freeze({
	count: 'items', countNonBlank: 'items', distinct: 'items', countYes: 'items',
	shareYes: 'share',
	sum: 'value', avg: 'value', median: 'value', min: 'value', max: 'value', latest: 'value',
});

// The ops whose buckets add up to a whole — the only ones a donut may show.
// An average, a median or a share is never a part of a whole (§4.2).
const PART_OF_WHOLE_OPS = ['count', 'countNonBlank', 'countYes', 'sum'];

// The words an op takes in a refusal: "Amount is text; it can't be totaled."
const OP_VERBS = Object.freeze({
	count: 'counted',
	countNonBlank: 'counted',
	distinct: 'counted by distinct value',
	sum: 'totaled',
	avg: 'averaged',
	median: 'given a median',
	min: 'given a smallest value',
	max: 'given a largest value',
	latest: 'given a latest value',
	countYes: 'counted as Yes',
	shareYes: 'shown as a share of Yes',
});

// ---------------------------------------------------------------- the buckets

// Time buckets reuse the list's grouping tokens (keepr-web src/utils/
// itemGroups.js). 15m, 20m, 30m and 3d are not offered (d22). A week starts
// on Sunday, as the list's does (q21).
const TIME_BUCKETS = ['hour', 'day', 'week', 'month', 'quarter', 'year'];
const DATE_BUCKETS = ['day', 'week', 'month', 'quarter', 'year'];
// The CYCLIC parts: a day of the week, an hour of the day, a month of the
// year. A union of ranges does not fit a query's 50 conditions ("every Monday
// for a year" is 104), so their doors are KQL's date parts (q20, PR AK):
// `created.weekday = mon`, `due.hour = 9`, `due.month = 3`. They lay out an
// ORDINAL axis, not a timeline (bucketAxis).
const CYCLIC_BUCKETS = ['weekday', 'hourOfDay', 'monthOfYear'];
// A `time` element is a wall clock with no date, so its hour of day is a
// RANGE on the stored "HH:mm" text, which KQL compares as a time — not a
// cyclic part of an instant. It needs no new term (A2a pins the door).
const CLOCK_BUCKETS = ['hourOfDay', 'halfHour'];
const ALL_BUCKETS = ['hour', 'day', 'week', 'month', 'quarter', 'year', 'weekday', 'hourOfDay', 'monthOfYear', 'halfHour'];

// The buckets a date element offers at each `precision`: a month-precision
// date has no day in it to group by.
const PRECISION_BUCKETS = Object.freeze({
	day: ['day', 'week', 'month', 'quarter', 'year'],
	month: ['month', 'quarter', 'year'],
	year: ['year'],
});
const PRECISION_CYCLIC = Object.freeze({
	day: ['weekday', 'monthOfYear'],
	month: ['monthOfYear'],
	year: [],
});

// ------------------------------------------------------------------ ranges

// A chart's date range is structured, not KQL: KQL's relative dates are UTC
// midnight, and a chart's day is its collection's (q06, §4.4).
const RANGE_PRESETS = ['today', 'last7d', 'last30d', 'last90d', 'last12m', 'thisWeek', 'thisMonth', 'thisQuarter', 'thisYear', 'lastMonth', 'lastYear', 'all'];
// The longest each preset can be, in days — for bounding buckets before a
// run, never for resolving the range (the engine does that, in the zone).
const PRESET_MAX_DAYS = Object.freeze({
	today: 1, last7d: 7, last30d: 30, last90d: 90, last12m: 366,
	thisWeek: 7, thisMonth: 31, thisQuarter: 92, thisYear: 366, lastMonth: 31, lastYear: 366, all: null,
});
// A custom range's years (§5.2): wide enough for any record, narrow enough
// that a range alone never makes an unbounded axis.
const CUSTOM_RANGE_YEARS = Object.freeze({ min: 1900, max: 2200 });
const DAY_MS = 86400000;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Milliseconds of a 'YYYY-MM-DD' day in UTC, or NaN for anything else (2026-02-30 included). */
function dayMs(text) {
	const m = YMD_RE.exec(typeof text === 'string' ? text : '');
	if (!m) return NaN;
	const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	const d = new Date(ms);
	return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]) ? ms : NaN;
}

/** Is this a real day written 'YYYY-MM-DD'? */
function isDay(text) {
	return Number.isFinite(dayMs(text));
}

/** The most days a range can span, or null when it is open (`all`, none). */
function rangeMaxDays(range) {
	if (!range) return null;
	if (typeof range.preset === 'string') return hasOwn(PRESET_MAX_DAYS, range.preset) ? PRESET_MAX_DAYS[range.preset] : null;
	const from = dayMs(range.from);
	const to = dayMs(range.to);
	if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null;
	return Math.round((to - from) / DAY_MS) + 1;
}

/**
 * The most buckets `bucket` can cut `range` into — an upper bound, so the
 * builder can coarsen a choice and the spec refuse one past LIMITS.buckets
 * before anything runs (§4.4). Null when the range is open: the engine counts
 * those from the data and coarsens then. A clock bucket has a fixed count.
 */
function estimateBuckets(range, bucket) {
	if (bucket === 'hourOfDay') return 24;
	if (bucket === 'halfHour') return 48;
	if (bucket === 'weekday') return 7;
	if (bucket === 'monthOfYear') return 12;
	const days = rangeMaxDays(range);
	if (days === null) return null;
	switch (bucket) {
		case 'hour': return days * 24 + 1;
		case 'day': return days + 1;
		case 'week': return Math.ceil(days / 7) + 1;
		case 'month': return Math.ceil(days / 28) + 1;
		case 'quarter': return Math.ceil(days / 90) + 1;
		case 'year': return Math.ceil(days / 365) + 1;
		default: return null;
	}
}

// The d06 readings of an element that holds several dates.
const MULTI_READINGS = ['earliest', 'latest', 'each'];
// "Each date in its own bar" has an exact door — an `in (…)` list of the
// bucket's dates — only on a date-only element and only for these buckets
// (a month is 31 values, one condition). Longer buckets and date-times draw
// no link (d28).
const EACH_DOOR_BUCKETS = ['day', 'week', 'month'];

// ---------------------------------------------------------- the measurements

// The measures the units registry defines (utils/units/registry.js). A
// measure this list does not know is treated as unknown: it may not total,
// because it might be affine.
const KNOWN_MEASURES = ['mass', 'length', 'temperature', 'volume', 'duration', 'area', 'speed', 'energy', 'pressure', 'data-size', 'count',
	'volume-rate', 'mass-rate', 'count-rate', 'angle', 'frequency', 'fuel-economy', 'torque'];
// Affine measures (a unit whose zero is not the base's zero): adding bases is
// meaningless, so they never total. Temperature has an offset; fuel economy
// has a reciprocal unit (0 L/100km has no base at all), and 30 mpg + 30 mpg is
// not 60 of anything. The formula module refuses sum() on them for the same
// reason.
const NEVER_TOTAL_MEASURES = ['temperature', 'fuel-economy'];
// How a refusal names one: "Mileage is a fuel economy; fuel economies are never totaled."
const NEVER_TOTAL_WORDS = { temperature: ['a temperature', 'temperatures'], 'fuel-economy': ['a fuel economy', 'fuel economies'] };
// Intensive measures: their natural default is an average by day, not a
// total by month (§4.2 "Default when picked"). A rate is one (a pump's
// readings over a month average to its rate; they do not add up to one), and
// so are a frequency, a fuel economy, an angle (a pitch, a range of motion)
// and a torque (a tightening spec, not an amount that accumulates). An average
// of headings is not a circular mean -- 350 and 10 degrees average to 180 --
// which the guide says; totals stay offered for an angle and for a torque.
const INTENSIVE_MEASURES = ['temperature', 'pressure', 'speed', 'volume-rate', 'mass-rate', 'count-rate', 'angle', 'frequency', 'fuel-economy', 'torque'];
// Measures whose units are all `convertible: false` (tablets, puffs, boxes…,
// and their rates: drops/min, tablets/day). Their values total only within
// ONE unit — the chart's — and values in another unit are noted, not added,
// so the formula module's silent `3 tablet + 2 puff = 5` is never repeated.
const PER_UNIT_MEASURES = ['count', 'count-rate'];

// ---------------------------------------------------------------- KQL names

// The names KQL reads as system fields before any element (utils/kql/kql.js
// SYSTEM_FIELDS), unless the name is quoted. A chart reads created, updated,
// card and collection as those system fields, as unquoted KQL does. An
// ELEMENT named after one of the others (set, tags, notes, id) may be
// measured — a measure has no door — but not grouped by or used as the
// chart's date, whose doors would name the system field. "Created by" is
// `created.by`, KQL's creator term (q20, PR AK): no element can be called that
// (an element name holds no dot), so `owner` stays an ordinary element name.
// `text` is KQL's free-text term (KPR-739): a door naming an element called
// that would open the search instead.
const KQL_SYSTEM_NAMES = ['card', 'set', 'tags', 'notes', 'created', 'updated', 'id', 'collection', 'text'];
const SYSTEM_FIELDS = ['created', 'updated', 'card', 'collection', 'created.by'];

// ------------------------------------------------------- KQL compile families

// How the FILTER compiles an element's values: the type dispatch of
// utils/kql/compile.js (NUMERIC_TYPES, DATE_TYPES, then measurement,
// currency, boolean, location, user and card-lookup, and compileText for
// everything else). A chart's element must compile the same way under the
// collection's vocabulary, where the first card to define a name wins,
// because its doors and the chart's filter are KQL read against that
// vocabulary (plan §4.1 ⚠, decision d37). Two cards that define a name in one
// family — a choice on one and plain text on another, a number and a rating —
// compile alike and do not collide. A measurement compares on `base`, and
// bases of two measures are different quantities, so each measure is its own
// family. A keepr-api test holds this map to compile.js's source.
const KQL_FAMILIES = Object.freeze({
	number: 'number', decimal: 'number', integer: 'number', rating: 'number',
	date: 'date', 'date-time': 'date', time: 'date',
	measurement: 'measurement',
	currency: 'currency',
	boolean: 'boolean',
	location: 'location',
	user: 'user',
	'card-lookup': 'lookup',
	// A file answers emptiness only (utils/kql/compile.js FILE).
	file: 'file',
	// A count of confirmations (KPR-390): a number whose blank reads as 0
	// (utils/kql/compileValues.js compileConfirmations) — so not `number`'s
	// family, where a blank is a blank.
	confirmations: 'confirmations',
	// A repeating group (KPR-681): a list of entries, read by `has (…)`, its
	// emptiness and `.count` (utils/kql/compile.js compileGroupCondition) —
	// nothing a text, a number or another group of other columns reads alike.
	// What a chart makes of one is KPR-684's: kind `group` below.
	'repeating-group': 'group',
});
// Everything else — choice, text-small (an inert list or none), text-large,
// rich-text, url, phone, email, color, and a type compile.js does not know —
// goes through compileText.
const KQL_TEXT_FAMILY = 'text';
const KQL_FAMILY_LABELS = Object.freeze({
	number: 'a number', date: 'a date', 'date:several': 'a list of dates', measurement: 'a measurement', currency: 'an amount of money',
	boolean: 'a yes/no', location: 'a location', user: 'a user', lookup: 'an item lookup', text: 'text',
	file: 'a file', confirmations: 'a count of confirmations', group: 'a repeating group',
});

/**
 * The family the filter compiles an element in: 'number', 'date',
 * 'date:several', 'measurement:mass', 'text'… A date or date-time element
 * holding SEVERAL dates is a family of its own: compileDate asks an equality
 * of one entry at a time (`$elemMatch`) only when the element it compiles
 * against allows several (utils/kql/compileValues.js dateEq), so a door
 * compiled against a one-date definition would match an item whose entries
 * straddle the day.
 */
function kqlFamilyOf(element) {
	const el = element || {};
	const family = hasOwn(KQL_FAMILIES, el.dataType) ? KQL_FAMILIES[el.dataType] : KQL_TEXT_FAMILY;
	if (family === 'date' && (el.dataType === 'date' || el.dataType === 'date-time') && optionsOf(el).allowMultiple === true) return 'date:several';
	if (family !== 'measurement') return family;
	const measure = optionsOf(el).measure;
	return `measurement:${typeof measure === 'string' ? measure : ''}`;
}

/** Do two definitions of one name compile alike in the filter (d37)? */
function sameKqlFamily(a, b) {
	return kqlFamilyOf(a) === kqlFamilyOf(b);
}

/** A family in words, for the collision refusal: "a mass measurement", "text". */
function kqlFamilyLabel(element) {
	const family = kqlFamilyOf(element);
	if (family.indexOf('measurement:') === 0) {
		const measure = family.slice('measurement:'.length);
		return measure ? `a ${measure} measurement` : 'a measurement';
	}
	return KQL_FAMILY_LABELS[family];
}

// ----------------------------------------------------------------- the kinds

// Every data type maps to a KIND; a kind says what may be measured and how it
// groups. Adjustments per element (auto-numbering, several values, a
// measure's class, a date's precision) are made in describe().
const TYPE_KINDS = Object.freeze({
	number: 'number', decimal: 'number', integer: 'number',
	currency: 'money',
	measurement: 'measurement',
	rating: 'rating',
	boolean: 'boolean',
	choice: 'choice',
	'card-lookup': 'lookup',
	user: 'user',
	date: 'date',
	'date-time': 'datetime',
	time: 'time',
	'text-small': 'text', email: 'text', url: 'text', phone: 'text',
	// A color is its "#rrggbb" text (keepr-web docs/plans/data-types-second-pass.md §4.1).
	color: 'text',
	'text-large': 'longText', 'rich-text': 'longText',
	location: 'location',
	// A file (keepr-web docs/plans/data-types-file-element.md) is not
	// chartable: an attachment id has nothing to measure or group by.
	file: 'file',
	// How many people said "me too" (KPR-390): a count.
	confirmations: 'confirmations',
	// A repeating group (KPR-684): a list of entries. A chart counts its
	// entries and reads its columns; the group itself is neither measured nor
	// grouped by (describeColumnForCharts).
	'repeating-group': 'group',
});

// Free text groups by its most common values only where a value repeats: a
// name or an address does, a URL or a phone number is an identifier. A color
// repeats by nature (a fleet's trim colors, a shelf's label colors).
const TOP_VALUE_TYPES = ['text-small', 'email', 'color'];

const KINDS = Object.freeze({
	number: { label: 'a number', measures: ['countNonBlank', 'distinct', 'sum', 'avg', 'median', 'min', 'max', 'latest'], group: 'bins', zeroable: true, defaultMeasure: 'sum', notes: ['numberAsText'] },
	sequence: { label: 'auto-numbering', measures: ['countNonBlank', 'distinct'], group: 'bins', zeroable: false, defaultMeasure: 'countNonBlank', notes: ['autoNumbering', 'numberAsText'] },
	money: { label: 'an amount of money', measures: ['sum', 'avg', 'median', 'min', 'max'], group: 'bins', zeroable: true, defaultMeasure: 'sum', notes: ['perCurrency'] },
	measurement: { label: 'a measurement', measures: ['sum', 'avg', 'median', 'min', 'max', 'latest'], group: 'bins', zeroable: true, defaultMeasure: 'sum', notes: ['baseOnly'] },
	rating: { label: 'a rating', measures: ['avg', 'median', 'min', 'max', 'latest'], group: 'rating', zeroable: true, defaultMeasure: 'avg', notes: ['neverTotals', 'wholeStars'] },
	boolean: { label: 'a yes/no', measures: ['countYes', 'shareYes'], group: 'boolean', zeroable: true, defaultMeasure: 'shareYes', notes: ['unsetIsBlank'] },
	choice: { label: 'a choice', measures: ['countNonBlank', 'distinct'], group: 'choice', zeroable: false, defaultMeasure: 'countNonBlank', notes: ['listOrder', 'caseInsensitive'] },
	lookup: { label: 'an item lookup', measures: ['distinct'], group: 'lookup', zeroable: false, defaultMeasure: 'distinct', notes: [] },
	user: { label: 'a user', measures: ['distinct'], group: 'user', zeroable: false, defaultMeasure: 'distinct', notes: [] },
	date: { label: 'a date', measures: ['min', 'max', 'countNonBlank'], group: 'time', zeroable: false, defaultMeasure: 'max', notes: [] },
	datetime: { label: 'a date and time', measures: ['min', 'max', 'countNonBlank'], group: 'time', zeroable: false, defaultMeasure: 'max', notes: [] },
	time: { label: 'a time of day', measures: ['min', 'max'], group: 'clock', zeroable: false, defaultMeasure: 'max', notes: ['wallClock'] },
	text: { label: 'text', measures: ['countNonBlank', 'distinct'], group: 'topValues', zeroable: false, defaultMeasure: 'countNonBlank', notes: ['caseInsensitive'] },
	longText: { label: 'long text', measures: ['countNonBlank'], group: null, zeroable: false, defaultMeasure: 'countNonBlank', notes: [] },
	location: { label: 'a location', measures: ['countNonBlank'], group: null, zeroable: false, defaultMeasure: 'countNonBlank', notes: ['notQueryable'] },
	file: { label: 'a file', measures: [], group: null, zeroable: false, defaultMeasure: null, notes: ['notChartable'] },
	// A blank IS zero here (nobody confirmed), and is read so (`blankIsZero`):
	// `zeroable` is false because there is no blank left to choose about.
	confirmations: { label: 'a count of confirmations', measures: ['sum', 'avg', 'median', 'min', 'max'], group: 'bins', zeroable: false, defaultMeasure: 'sum', notes: ['blankIsZero'] },
	// A repeating group (KPR-684): count its entries (`entries` on the spec),
	// or measure and group by its columns — never the list itself.
	group: { label: 'a repeating group', measures: [], group: null, zeroable: false, defaultMeasure: null, notes: ['countEntries'] },
	unknown: { label: 'a kind of value charts do not know', measures: [], group: null, zeroable: false, defaultMeasure: null, notes: ['unknownType'] },
});

// A group kind: the axis it lays out along (§5.3 `axis.kind`), whether its
// empty buckets are filled (§5.3: only time, choice, rating and bin
// categories are), and how many categories it may keep.
const GROUP_KINDS = Object.freeze({
	bins: { axis: 'bins', filled: true },
	rating: { axis: 'ordinal', filled: true },
	boolean: { axis: 'category', filled: true },
	choice: { axis: 'category', filled: true, top: { default: null, max: LIMITS.categories } },
	lookup: { axis: 'category', filled: false, top: { default: LIMITS.defaultTop, max: LIMITS.categories } },
	user: { axis: 'category', filled: false, top: { default: LIMITS.defaultTop, max: LIMITS.categories } },
	topValues: { axis: 'category', filled: false, top: { default: LIMITS.defaultTop, max: LIMITS.topValues } },
	time: { axis: 'time', filled: true },
	clock: { axis: 'ordinal', filled: true },
	card: { axis: 'category', filled: false },
	collection: { axis: 'category', filled: false },
	// Who added an item (`created.by`): only the people the matched items name,
	// never a roster (§5.3), the most counted first.
	owner: { axis: 'category', filled: false, top: { default: LIMITS.defaultTop, max: LIMITS.categories } },
});

// ------------------------------------------------------------------ helpers

const has = (list, value) => list.indexOf(value) !== -1;
const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);
const optionsOf = el => (el && el.options && typeof el.options === 'object' ? el.options : {});
const without = (list, drop) => list.filter(x => !has(drop, x));
const copy = list => list.slice();

function isSequence(options) {
	return !!(options.sequence && typeof options.sequence === 'object');
}

function precisionOf(options) {
	return hasOwn(PRECISION_BUCKETS, options.precision) ? options.precision : 'day';
}

function currenciesOf(options) {
	const out = [];
	(Array.isArray(options.currencies) ? options.currencies : []).forEach(code => {
		if (typeof code === 'string' && code && out.indexOf(code) === -1) out.push(code);
	});
	return out;
}

/**
 * The measures a measurement element allows when that is MORE than one
 * (KPR-1085: `measures` naming the primary `measure` and another), else null
 * -- the units module's allowedMeasures, read without the registry (this file
 * requires nothing).
 */
function severalMeasuresOf(options) {
	const primary = typeof options.measure === 'string' && options.measure ? options.measure : null;
	if (!primary) return null;
	const list = Array.isArray(options.measures) ? options.measures : null;
	const out = [primary];
	if (list && list.indexOf(primary) !== -1) list.forEach(id => { if (typeof id === 'string' && id && out.indexOf(id) === -1) out.push(id); });
	// An element's OWN units (KPR-1109, `ownUnits`): each a measure of one
	// unit (its id, `own:<slug>`), so 2 smidgens and 2 each have no total.
	(Array.isArray(options.ownUnits) ? options.ownUnits : []).forEach(own => {
		const id = own && typeof own === 'object' ? own.id : null;
		if (typeof id === 'string' && id.indexOf('own:') === 0 && id.length > 4 && out.indexOf(id) === -1) out.push(id);
	});
	return out.length > 1 ? out : null;
}

/** A measure id in words: an own unit (KPR-1109) by its id's words, "own:baker-s-dozen" -> "baker s dozen". */
function measureWords(id) {
	return typeof id === 'string' && id.indexOf('own:') === 0 ? id.slice(4).replace(/-/g, ' ') : id;
}

/** The rating scale: `max` (legacy `count`), 5 when unset — the row validator's rule. */
function ratingScaleOf(options) {
	const max = Number(options.max) > 0 ? Number(options.max) : (Number(options.count) > 0 ? Number(options.count) : 5);
	return Math.floor(max);
}

/** A group description for `kind`, with its buckets when it has any. */
function groupOf(kind, extra) {
	const base = GROUP_KINDS[kind];
	const out = { kind, axis: base.axis, filled: base.filled };
	if (base.top) out.top = { default: base.top.default, max: base.top.max };
	if (extra) Object.keys(extra).forEach(key => { out[key] = extra[key]; });
	return out;
}

// ------------------------------------------------------------ describe()

/**
 * describeElementForCharts(element, ctx) — what a chart may ask of one
 * element of the chart's card: `{ field, dataType, kind, label, multiple,
 * measures, zeroable, groups, defaultMeasure, time, multi, notes, … }`.
 *
 *   measures        the ops that take this element, in the order the builder
 *                   offers them (`count`, which counts items, is always
 *                   available and is not listed here)
 *   zeroable        whether "Count as zero" (blanks: 'zero', d05) means
 *                   anything for it — a blank date is not day zero
 *   groups          [] or [one group]: how the element lays out an axis —
 *                   `{ kind, axis, filled, top?, buckets?, waiting?,
 *                   defaultBucket?, values? }`; `waiting` lists the buckets
 *                   that are not offered yet, and why
 *   time            whether it may be the chart's `time.on`
 *   multi           the d06 readings for an element holding several dates
 *   notes           codes the builder and the chart explain (SCHEMA.md
 *                   "Charts" lists them)
 *
 * `ctx` is accepted for symmetry with describeSystemField; an element's
 * capabilities are its own. A data type this table does not know answers
 * kind 'unknown' with nothing offered: fail closed.
 */
function describeElementForCharts(element, ctx) { // eslint-disable-line no-unused-vars
	const el = element || {};
	const options = optionsOf(el);
	const dataType = typeof el.dataType === 'string' ? el.dataType : null;
	let kind = hasOwn(TYPE_KINDS, dataType) ? TYPE_KINDS[dataType] : 'unknown';
	if (kind === 'number' && isSequence(options)) kind = 'sequence';
	// A choice list means something only on a choice (the owner's ruling,
	// 2026-10-19, KPE-033): a short text that still carries one — kept, inert,
	// for a change back to a choice — charts as the text it is. Until KPR-1014
	// it charted as a choice (plan §2: most real choices were text with a
	// list); KPR-1013's migration made every such element a choice first.
	const spec = KINDS[kind];

	const out = {
		field: typeof el.name === 'string' ? el.name : null,
		dataType,
		kind,
		label: spec.label,
		multiple: false,
		measures: copy(spec.measures),
		zeroable: spec.zeroable,
		groups: [],
		defaultMeasure: spec.defaultMeasure,
		time: false,
		multi: null,
		notes: copy(spec.notes),
	};

	if (kind === 'money') {
		const currencies = currenciesOf(options);
		out.currencies = currencies.length ? currencies : null;
		if (currencies.length !== 1) out.notes.push('severalCurrencies');
	}

	const several = kind === 'measurement' ? severalMeasuresOf(options) : null;
	if (several) {
		// An element that allows several measures (KPR-1085 ⚑): 2 cups and
		// 250 g have no total, average or range on one axis, so a chart
		// measures nothing of its values (per-measure charts are a follow-up).
		// Its items are still counted ("Count items"), grouped by anything else.
		out.measure = several[0];
		out.severalMeasures = several;
		out.measures = [];
		out.defaultMeasure = null;
		out.zeroable = false;
		out.notes.push('severalMeasures');
	} else if (kind === 'measurement') {
		const measure = typeof options.measure === 'string' && options.measure ? options.measure : null;
		out.measure = measure;
		if (!measure || !has(KNOWN_MEASURES, measure)) {
			// It might be affine; a total could be meaningless, and so could a
			// blank read as zero. Fail closed.
			out.measures = without(out.measures, ['sum']);
			out.defaultMeasure = 'avg';
			out.zeroable = false;
			out.notes.push('unknownMeasure');
		} else {
			if (has(NEVER_TOTAL_MEASURES, measure)) {
				// An affine zero is a real temperature (0 °C), not "nothing", and
				// a fuel economy of zero does not exist: "Count as zero" would
				// invent a reading.
				out.measures = without(out.measures, ['sum']);
				out.zeroable = false;
				out.notes.push('neverTotals');
			}
			if (has(INTENSIVE_MEASURES, measure)) {
				out.defaultMeasure = 'avg';
				out.notes.push('intensive');
			}
			if (has(PER_UNIT_MEASURES, measure)) out.notes.push('perUnit');
		}
	}

	// Count units share one base (2 tablets and 2 puffs are both 2), and KQL
	// compares `base`, so a range of one count unit cannot open its items
	// alone: its bins are counted in the element's default unit, with no door.
	if (spec.group === 'bins' && !several) out.groups.push(has(out.notes, 'perUnit') ? groupOf('bins', { perUnit: true }) : groupOf('bins'));
	if (spec.group === 'rating') out.groups.push(groupOf('rating', { values: ratingScaleOf(options) }));
	if (spec.group === 'boolean') out.groups.push(groupOf('boolean'));
	if (spec.group === 'lookup') out.groups.push(groupOf('lookup'));
	if (spec.group === 'user') out.groups.push(groupOf('user'));
	if (spec.group === 'choice') out.groups.push(groupOf('choice'));
	if (spec.group === 'topValues' && has(TOP_VALUE_TYPES, dataType)) out.groups.push(groupOf('topValues'));
	if (spec.group === 'clock') out.groups.push(groupOf('clock', { buckets: copy(CLOCK_BUCKETS), defaultBucket: 'hourOfDay' }));

	if (kind === 'date' || kind === 'datetime') {
		out.time = true;
		const precision = kind === 'date' ? precisionOf(options) : 'day';
		const linear = kind === 'date' ? copy(PRECISION_BUCKETS[precision]) : copy(TIME_BUCKETS);
		const cyclic = kind === 'date' ? copy(PRECISION_CYCLIC[precision]) : copy(CYCLIC_BUCKETS);
		out.groups.push(groupOf('time', {
			buckets: linear.concat(cyclic),
			defaultBucket: has(linear, 'month') ? 'month' : linear[0],
		}));
		if (kind === 'date' && precision !== 'day') {
			out.precision = precision;
			out.notes.push('precisionFloor');
		}
	}

	// Several values. Dates ask the author how to read them (d06); lookups,
	// users and the multi-choice slot count an item once in each value.
	if (options.allowMultiple === true) {
		if (kind === 'date' || kind === 'datetime') {
			out.multiple = true;
			out.multi = copy(MULTI_READINGS);
			out.notes.push('severalDates');
		} else if (kind === 'lookup' || kind === 'user' || kind === 'choice') {
			out.multiple = true;
			out.notes.push('multiValued');
		}
	}

	return out;
}

// --------------------------------------------------------- a group's columns

// What a chart counts (KPR-684): the items, or the entries of one repeating
// group of the chart's card (the spec's `entries`).
const COUNTINGS = ['items', 'entries'];

/** What a person calls a group in a sentence: its singular label, else its name. */
function groupWords(group) {
	const g = group || {};
	const singular = g.label && typeof g.label.singular === 'string' ? g.label.singular.trim() : '';
	if (singular) return singular;
	return typeof g.name === 'string' && g.name ? g.name : 'the group';
}

/**
 * describeColumnForCharts(column, group, { counting, entryDate }) — what a
 * chart may ask of a COLUMN of a repeating group (KPR-684): describeElementForCharts'
 * shape, `field` written `<group>.<column>` (an element name holds no dot),
 * with `group`, `column`, `groupLabel` and `counting` beside it.
 *
 *   counting 'entries' (default)  the chart counts the group's entries, each
 *                       entry a record: the column's type offers what it
 *                       offers on an item, a date's day of the week, hour of
 *                       the day (a date-time's) and month of the year
 *                       included — each bar's door is the column's KQL date
 *                       part inside the block (KPR-935) — but on a column
 *                       holding several dates in an entry, whose part KQL
 *                       does not read inside a block. `latest` only with
 *                       `entryDate`: the chart's date is a date column of
 *                       this group, so each entry has a date of its own to be
 *                       the newest by (KPR-806); without it an item's entries
 *                       share its date and none is the latest (note
 *                       `latestNeedsEntryDate`, the refusal says what to do).
 *                       A date or date-time column holding ONE date is the
 *                       chart's date (`time: true`, KPR-806): each entry is
 *                       placed by its own value. One holding several dates
 *                       in an entry is not (`time: false`).
 *   counting 'items'    the chart counts items: a column only GROUPS, and an
 *                       item counts once in each value its entries hold, as a
 *                       value list does (`multiple`; several dates are read
 *                       as the earliest, the latest or each — a cyclic part
 *                       opens its items only read as each, doorFor); nothing is
 *                       measured — the refusal says to count the entries —
 *                       and it is never the chart's date (`time: false`).
 *
 * A column type the table does not know answers as describeElementForCharts
 * does: nothing offered.
 */
function describeColumnForCharts(column, group, options) {
	const counting = options && options.counting === 'items' ? 'items' : 'entries';
	const entryDate = !!(options && options.entryDate === true);
	const g = group || {};
	const groupName = typeof g.name === 'string' && g.name ? g.name : null;
	const out = describeElementForCharts(column);
	out.field = groupName !== null && out.field !== null ? `${groupName}.${out.field}` : null;
	out.group = groupName;
	out.column = column && typeof column.name === 'string' ? column.name : null;
	out.groupLabel = groupWords(g);
	out.counting = counting;
	// One date in each entry places that entry (KPR-806); several, or a chart
	// that counts items, never does.
	out.time = counting === 'entries' && out.time === true && !out.multiple;
	out.notes.push('column');
	// A column holding SEVERAL dates in an entry keeps its timeline but has no
	// day of the week, hour of the day or month of the year: KQL reads no part
	// of such a column inside a block (KPR-935 — per entry it would cost
	// entries × dates), so no door could open those bars.
	if (out.multiple && (out.kind === 'date' || out.kind === 'datetime')) {
		out.groups = out.groups.map(gr => {
			if (gr.kind !== 'time' || !gr.buckets) return gr;
			const kept = without(gr.buckets, CYCLIC_BUCKETS);
			return groupOf(gr.kind, { buckets: kept, defaultBucket: has(kept, gr.defaultBucket) ? gr.defaultBucket : kept[0] });
		});
	}
	if (counting === 'entries') {
		if (!entryDate && has(out.measures, 'latest')) {
			out.measures = without(out.measures, ['latest']);
			out.notes.push('latestNeedsEntryDate');
		}
		return out;
	}
	out.measures = [];
	out.defaultMeasure = null;
	out.zeroable = false;
	if (!out.multiple && out.groups.length) {
		out.multiple = true;
		if (out.kind === 'date' || out.kind === 'datetime') {
			out.multi = copy(MULTI_READINGS);
			out.notes.push('severalDates');
		} else out.notes.push('multiValued');
	}
	return out;
}

/**
 * describeSystemField(name, ctx) — the system fields a chart may use: the
 * same shape as describeElementForCharts, `system: true`, or null for a name
 * that is not one.
 *
 *   ctx.cardId              the chart's card (null: every card, d20)
 *   ctx.cardHasDescendants  whether that card has child cards
 *   ctx.hasSubCollections   whether the chart's collection has sub-collections
 *
 * `card` groups only when the chart counts every card or a card with
 * descendants, and `collection` only in a collection with sub-collections:
 * otherwise each is one bar. `created.by` is who added an item (KQL's creator
 * term, q20, PR AK): grouped by, never measured. Tags are not a field here yet.
 */
function describeSystemField(name, ctx) {
	const c = ctx || {};
	const base = { field: name, dataType: null, system: true, kind: null, label: null, multiple: false, measures: [], zeroable: false, groups: [], defaultMeasure: null, time: false, multi: null, notes: [] };
	if (name === 'created' || name === 'updated') {
		base.kind = 'datetime';
		base.label = name === 'created' ? 'the date an item was added' : 'the date an item was last changed';
		base.measures = ['min', 'max'];
		base.defaultMeasure = 'max';
		base.time = true;
		base.groups.push(groupOf('time', {
			buckets: TIME_BUCKETS.concat(CYCLIC_BUCKETS),
			defaultBucket: 'month',
		}));
		// KQL's `created` compiles createdAt only; a row with only a legacy
		// spelling of its timestamp is noted, never counted by a door that
		// could not open it (§4.2).
		base.notes.push('canonicalTimestampOnly');
		return base;
	}
	if (name === 'card') {
		base.kind = 'card';
		base.label = 'the card';
		if (c.cardId === null || c.cardId === undefined || c.cardHasDescendants === true) base.groups.push(groupOf('card'));
		else base.notes.push('oneCard');
		return base;
	}
	if (name === 'collection') {
		base.kind = 'collection';
		base.label = 'the collection';
		if (c.hasSubCollections === true) base.groups.push(groupOf('collection'));
		else base.notes.push('oneCollection');
		return base;
	}
	if (name === 'created.by') {
		base.kind = 'owner';
		base.label = 'who added an item';
		base.groups.push(groupOf('owner'));
		return base;
	}
	return null;
}

/**
 * The axis a group lays out for `bucket` (§5.3 `axis.kind`): a cyclic part —
 * a day of the week, an hour of the day, a month of the year — is an ORDINAL
 * axis of fixed values, not a timeline, whatever the group's own axis is.
 */
function bucketAxis(group, bucket) {
	if (has(CYCLIC_BUCKETS, bucket)) return 'ordinal';
	return group && group.axis ? group.axis : null;
}

// -------------------------------------------------------------- refusals

/**
 * The plain sentence for an op this table does not allow on an element:
 * "Amount is text; it can't be totaled." `name` is what the person calls the
 * element (its label, else its name). Null when the op IS allowed.
 */
function refusalFor(op, description, name) {
	const d = description || {};
	const who = name || d.field || 'This element';
	if (has(d.measures || [], op)) return null;
	if (!has(OPS, op)) return `"${op}" is not something a chart can measure.`;
	if (has(ITEM_OPS, op)) return `${who} can't be used there: "Count items" counts items, not an element.`;
	// A repeating group's column (KPR-684, describeColumnForCharts).
	if (d.column && d.counting === 'items') {
		return `${who} belongs to each ${d.groupLabel} entry, so it can be ${OP_VERBS[op]} only in a chart that counts ${d.groupLabel} entries.`;
	}
	if (d.column && op === 'latest' && has(d.notes, 'latestNeedsEntryDate')) {
		return `${who} belongs to each ${d.groupLabel} entry, and an item's entries share its date, so none of them is the latest. Make a date of each ${d.groupLabel} entry the chart's date to find the latest one.`;
	}
	if (d.kind === 'group') return `${who} is a repeating group; count its entries, or measure one of its columns.`;
	if (d.kind === 'measurement' && has(d.notes || [], 'severalMeasures')) {
		const list = (d.severalMeasures || []).map(measureWords);
		const words = list.length > 1 ? `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}` : 'several measures';
		return `${who} holds values in ${words}, which have no ${op === 'sum' ? 'total' : 'one scale'} between them, so a chart can't measure it. Count items instead.`;
	}
	if (op === 'sum' && d.kind === 'rating') return `${who} is a rating; ratings are never totaled. Average it instead.`;
	if (op === 'sum' && d.kind === 'measurement' && has(NEVER_TOTAL_MEASURES, d.measure)) {
		const words = NEVER_TOTAL_WORDS[d.measure];
		return `${who} is ${words[0]}; ${words[1]} are never totaled. Average it instead.`;
	}
	if (op === 'sum' && d.kind === 'measurement') return `${who} is a measurement whose kind charts do not know, so it can't be totaled.`;
	if (d.kind === 'sequence') return `${who} is auto-numbering; it can only be counted.`;
	if (!d.measures || !d.measures.length) return `${who} is ${d.label || 'a value charts do not know'}; a chart can't measure it.`;
	return `${who} is ${d.label}; it can't be ${OP_VERBS[op]}.`;
}

// --------------------------------------------------------------- answers

/**
 * What a measure answers, for the one-axis rule (§4.2: measures in different
 * units or currencies are never drawn on one axis) and for the forms:
 * `{ result, axis, mixed }`.
 *
 *   result  'items' | 'share' | 'number' | 'money' | 'measure' | 'date' | 'time'
 *   axis    two measures share an axis only when this is equal
 *   mixed   the answer may hold several currencies or units, so it cannot be
 *           one bar, one slice or one colour ramp
 *
 * `options.unit` is the measure's chart unit (a count unit's totals are per
 * unit).
 */
function measureAnswer(op, description, options) {
	const d = description || {};
	const o = options || {};
	const answer = OP_ANSWERS[op];
	if (answer === 'items') return { result: 'items', axis: 'number', mixed: false };
	if (answer === 'share') return { result: 'share', axis: 'share', mixed: false };
	if (answer !== 'value') return { result: null, axis: null, mixed: false };
	switch (d.kind) {
		case 'number': case 'sequence': case 'rating': case 'confirmations':
			return { result: 'number', axis: 'number', mixed: false };
		case 'money':
			return d.currencies && d.currencies.length === 1
				? { result: 'money', axis: `money:${d.currencies[0]}`, mixed: false }
				: { result: 'money', axis: 'money:*', mixed: true };
		case 'measurement':
			if (!d.measure) return { result: 'measure', axis: 'measure:*', mixed: true };
			if (has(PER_UNIT_MEASURES, d.measure)) return { result: 'measure', axis: `measure:${d.measure}:${o.unit || '*'}`, mixed: false };
			return { result: 'measure', axis: `measure:${d.measure}`, mixed: false };
		case 'date': case 'datetime':
			return { result: 'date', axis: 'date', mixed: false };
		case 'time':
			return { result: 'time', axis: 'time', mixed: false };
		default:
			return { result: null, axis: null, mixed: false };
	}
}

// ----------------------------------------------------------------- forms

const FORMS = ['number', 'progress', 'bar', 'line', 'area', 'donut', 'heatmap', 'calendar', 'table'];

// The options each form takes (§7). Anything else on `show` is refused. The
// top N is `groupBy.top`, and the previous period is `time.compare`.
const FORM_OPTIONS = Object.freeze({
	number: ['lowerIsBetter', 'sparkline'],
	progress: ['goal', 'lowerIsBetter'],
	bar: ['orientation', 'stack', 'sort'],
	line: ['cumulative', 'markers'],
	area: ['stack'],
	donut: [],
	heatmap: [],
	calendar: [],
	table: ['sort'],
});
// The closed values of the enumerated options.
const OPTION_VALUES = Object.freeze({
	orientation: ['auto', 'vertical', 'horizontal'],
	stack: ['none', 'stacked', 'percent'],
	sort: ['group', 'value'],
});
// The forms a previous-period comparison is drawn on (§4.1 `compare`).
const COMPARE_FORMS = ['number', 'line'];

function heatmapFits(group, split) {
	const g = group && typeof group.count === 'number' ? group.count : null;
	const s = split && typeof split.count === 'number' ? split.count : null;
	if (g !== null && g > LIMITS.heatmapAxis) return false;
	if (s !== null && s > LIMITS.heatmapAxis) return false;
	if (g !== null && s !== null && g * s > LIMITS.heatmapCells) return false;
	return true;
}

/**
 * formsForShape(shape) — the chart forms an answer of this shape may take,
 * the first being the default (§4.2, second table). `[]` when none can.
 *
 *   shape.measures  [{ op, result, axis, mixed, hidden }] — every measure,
 *                   formula measures included (a formula measure has no
 *                   `op`); measureAnswer() gives the rest. A `hidden` measure
 *                   is computed for a formula to read but not drawn (d36), so
 *                   only the VISIBLE ones decide the forms and the one-axis
 *                   rule: "average order value" over a hidden total and a
 *                   hidden count is one line
 *   shape.group     null | { axis: 'time'|'category'|'ordinal'|'bins', bucket?, count? }
 *   shape.split     null | { axis, count? }
 *
 * `count`, when known, is how many values the axis holds; it decides a heat
 * map's own cap (each axis ≤ 60, ≤ 2,000 cells — not the series cap) and
 * whether a split has more values than series.
 *
 * The table is the twin of every chart and is always last.
 */
function formsForShape(shape) {
	const s = shape || {};
	const measures = (Array.isArray(s.measures) ? s.measures : []).filter(m => m && m.hidden !== true);
	const group = s.group || null;
	const split = s.split || null;
	if (!measures.length) return [];
	if (split && !group) return [];

	// A date or a time is an ANSWER here (Earliest date), not a quantity: a
	// bar's length must say how much, so a date answer is a number tile or a
	// table.
	const dated = measures.some(m => m.result === 'date' || m.result === 'time');
	if (dated) return group ? ['table'] : ['number', 'table'];

	const axes = [];
	measures.forEach(m => { if (axes.indexOf(m.axis) === -1) axes.push(m.axis); });
	const anyMixed = measures.some(m => m.mixed);

	if (measures.length > 1) {
		// One axis, never two (§6.8): measures in different units or
		// currencies are refused together and made separate charts.
		if (axes.length > 1 || anyMixed || !group || split) return ['table'];
		return group.axis === 'time' ? ['bar', 'line', 'table'] : ['bar', 'table'];
	}

	const m = measures[0];
	const partOfWhole = !!m.op && has(PART_OF_WHOLE_OPS, m.op) && !m.mixed;
	const rampable = !m.mixed;

	if (!group) return ['number', 'progress', 'table'];

	if (!split) {
		if (group.axis === 'time') {
			const out = ['line', 'bar', 'area'];
			if (group.bucket === 'day') out.push('calendar');
			out.push('table');
			return out;
		}
		if (group.axis === 'bins') return ['bar', 'table'];
		return partOfWhole ? ['bar', 'donut', 'table'] : ['bar', 'table'];
	}

	// More split values than series: a heat map shows them all, so it leads;
	// a bar still works, folding the rest into Other (§4.7), so it stays.
	const heat = rampable && heatmapFits(group, split);
	const manySeries = typeof split.count === 'number' && split.count > LIMITS.series;
	const out = manySeries && heat ? ['heatmap', 'bar'] : ['bar'];
	if (group.axis === 'time') out.push('line');
	if (heat && out.indexOf('heatmap') === -1) out.push('heatmap');
	out.push('table');
	return out;
}

// -------------------------------------------------------------- defaults

/**
 * defaultChartFor(description, { role }) — the chart picking an element
 * proposes (§4.2, "Default when picked"): `{ measures: [{ op, element? }],
 * groupBy, show: { type } }`, or null where the table has no default.
 *
 *   role 'measure' (default)  the element was picked as what to chart
 *   role 'group'              the element was picked as what to group by:
 *                             count items by it
 *
 * The time axis is `created`, the default time axis (§4.2).
 */
function defaultChartFor(description, options) {
	const d = description || {};
	const role = (options && options.role) || 'measure';
	const field = d.field;
	const group = d.groups && d.groups[0] ? d.groups[0] : null;

	const byItself = (g, on) => {
		const out = { on };
		if (g.defaultBucket) out.bucket = g.defaultBucket;
		if (g.top && g.top.default) out.top = g.top.default;
		// Ranges have no size of their own and the spec asks for one
		// (bins_required): ten equal ranges, from the smallest value to the
		// largest, until the author says otherwise.
		if (g.kind === 'bins') out.bins = { count: DEFAULT_BINS };
		return out;
	};
	const finish = (measure, groupBy, groupAxis, bucket) => {
		const answer = measureAnswer(measure.op, measure.op === 'count' ? {} : d);
		const shape = {
			measures: [{ op: measure.op, result: answer.result, axis: answer.axis, mixed: answer.mixed }],
			group: groupBy ? { axis: groupAxis, bucket } : null,
			split: null,
		};
		return { measures: [measure], groupBy: groupBy || null, show: { type: formsForShape(shape)[0] } };
	};
	const countBy = () => {
		if (!group) return null;
		const groupBy = byItself(group, field);
		return finish({ op: 'count' }, groupBy, group.axis, groupBy.bucket);
	};
	const overTime = (op, bucket) => finish({ op, element: field }, { on: 'created', bucket }, 'time', bucket);
	const alone = op => finish({ op, element: field }, null);

	// A column, counting items, only groups (describeColumnForCharts).
	if (role === 'group' || (d.column && d.counting === 'items')) return countBy();

	switch (d.kind) {
		case 'number': case 'money': return overTime('sum', 'month');
		// How many confirmations there are, in all: one number.
		case 'confirmations': return alone('sum');
		case 'measurement': return has(d.measures, 'sum') && !has(d.notes, 'intensive') ? overTime('sum', 'month') : overTime('avg', 'day');
		case 'sequence': return alone('countNonBlank');
		case 'rating': return alone('avg');
		case 'boolean': return alone('shareYes');
		case 'choice': case 'lookup': case 'user': case 'date': case 'datetime': case 'time': return countBy();
		case 'text': case 'longText': return alone('countNonBlank');
		default: return null;
	}
}

// ---------------------------------------------------------------- folding

/**
 * drawLimit(role, form) — the most marks an axis may draw (§4.7): a split's
 * series 6 (the palette's colours), a donut's parts 6, a category axis 50, a heat map axis 60 (a heat
 * map does not fold: past that it is not offered). Values past the limit fold
 * into ONE "Other" — never a refusal — and "Not set" is a mark too.
 */
function drawLimit(role, form) {
	if (form === 'heatmap') return LIMITS.heatmapAxis;
	if (form === 'donut' && role === 'group') return LIMITS.donutParts;
	if (role === 'split') return LIMITS.series;
	return LIMITS.categories;
}

/**
 * foldFor({ values, top, limit, blanks }) -> { kept, other, notSet, drawn } —
 * how an axis of `values` values fits `limit` marks: everything, when the
 * values and "Not set" (unless blanks are hidden) fit and no `top` cuts them;
 * otherwise the kept values (at most `top`), one Other and Not set within the
 * limit. `values` null means only a run knows (a lookup, a user, top text
 * values): the most that can be kept, with an Other.
 */
function foldFor(o) {
	const opts = o || {};
	const limit = typeof opts.limit === 'number' ? opts.limit : LIMITS.categories;
	const notSet = opts.blanks === 'hide' ? 0 : 1;
	const top = typeof opts.top === 'number' ? opts.top : null;
	const values = typeof opts.values === 'number' ? opts.values : null;
	if (values !== null && values + notSet <= limit && (top === null || values <= top)) {
		return { kept: values, other: 0, notSet, drawn: values + notSet };
	}
	let kept = Math.max(0, limit - 1 - notSet);
	if (top !== null) kept = Math.min(kept, top);
	if (values !== null) kept = Math.min(kept, values);
	const other = values === null || values > kept ? 1 : 0;
	return { kept, other, notSet, drawn: kept + other + notSet };
}

/**
 * drawnCount({ values, top, keep, blanks }) — how many values an axis holds
 * for formsForShape's counts: its values up to `top` (and `keep`), one Other
 * when some are cut, and "Not set" unless blanks are hidden. `values` null
 * means not known before a run: `top` + Other + Not set. Null when nothing
 * bounds it.
 */
function drawnCount(o) {
	const opts = o || {};
	const keep = typeof opts.keep === 'number' ? opts.keep : LIMITS.categories;
	const cap = typeof opts.top === 'number' ? Math.min(opts.top, keep) : keep;
	const notSet = opts.blanks === 'hide' ? 0 : 1;
	if (typeof opts.values !== 'number') return typeof opts.top === 'number' ? cap + 1 + notSet : null;
	const kept = Math.min(opts.values, cap);
	return kept + (opts.values > kept ? 1 : 0) + notSet;
}

// ----------------------------------------------------------------- doors

// Why a bucket cannot open an exact list (§5.4, d28). The chart draws no
// link there and says this instead.
const DOOR_REASONS = Object.freeze({
	severalDatesReading: 'Items with several dates are placed here by their earliest or latest one, and a list can\'t filter by that.',
	eachDateTime: 'Items with several date-times can land in more than one bar, and a list can\'t match just one of their times.',
	eachLongBucket: 'A quarter or a year holds too many dates for one list to match. Group by day, week or month to open the items.',
	otherSeveral: '"Other" can\'t open a list here: an item holding one shown value and one other would be left out.',
	currencyCode: 'A list can\'t filter by currency yet.',
	unit: 'A list can\'t filter by unit yet.',
	unitBins: 'Values in different count units look alike to a list (2 tablets and 2 puffs are both 2), so a range can\'t open just its items.',
});

/**
 * doorFor(kind, options) — can a bucket of this group open EXACTLY its items
 * in the list? `{ exact: true }`, or `{ exact: false, reason, message }`.
 *
 *   kind     a group kind (bins, rating, boolean, choice, lookup, user,
 *            topValues, time, clock, card, collection, owner) or one of the
 *            splits no path in KQL reaches: 'currencyCode', 'unit'
 *   options  { dataType, multiple, multi, bucket, other, perUnit }
 *            `other: true` asks about the "Other" bucket of the group;
 *            `perUnit: true` a range of a count-unit measurement
 *
 * A door is the items IN the bucket (§5.4), never the items that fed its
 * measure. Lookup equality by id and a card's or collection's own bucket
 * (`card = X and not card in (…children)`) are exact here; A2a confirms both
 * with the parity test before building on them, and ships without the door
 * if either fails.
 */
function doorFor(kind, options) {
	const o = options || {};
	const no = reason => ({ exact: false, reason, message: DOOR_REASONS[reason] });
	if (kind === 'currencyCode') return no('currencyCode');
	if (kind === 'bins' && o.perUnit) return no('unitBins');
	if (kind === 'unit') return no('unit');
	if (kind === 'time') {
		if (!o.multiple) return { exact: true };
		// A cyclic part's door asks ANY entry (`dues.weekday = mon`), which is
		// exactly "each date in its own bar", on a date and a date-time alike;
		// an earliest or latest reading it cannot say.
		if (has(CYCLIC_BUCKETS, o.bucket)) return o.multi === 'each' ? { exact: true } : no('severalDatesReading');
		// KQL matches ANY entry, and `d >= A and d < B` bounds each entry
		// separately, so a range cannot say "this item's earliest date".
		if (o.multi !== 'each') return no('severalDatesReading');
		if (o.dataType !== 'date') return no('eachDateTime');
		if (!has(EACH_DOOR_BUCKETS, o.bucket)) return no('eachLongBucket');
		return { exact: true };
	}
	if (o.other === true && o.multiple) return no('otherSeveral');
	return { exact: true };
}

module.exports = {
	LIMITS,
	DEFAULT_BINS,
	OPS,
	ITEM_OPS,
	OP_ANSWERS,
	PART_OF_WHOLE_OPS,
	OP_VERBS,
	TIME_BUCKETS,
	DATE_BUCKETS,
	CYCLIC_BUCKETS,
	CLOCK_BUCKETS,
	ALL_BUCKETS,
	PRECISION_BUCKETS,
	RANGE_PRESETS,
	PRESET_MAX_DAYS,
	CUSTOM_RANGE_YEARS,
	MULTI_READINGS,
	EACH_DOOR_BUCKETS,
	KNOWN_MEASURES,
	NEVER_TOTAL_MEASURES,
	INTENSIVE_MEASURES,
	PER_UNIT_MEASURES,
	KQL_SYSTEM_NAMES,
	SYSTEM_FIELDS,
	KQL_FAMILIES,
	KQL_TEXT_FAMILY,
	KQL_FAMILY_LABELS,
	TYPE_KINDS,
	KINDS,
	GROUP_KINDS,
	FORMS,
	FORM_OPTIONS,
	OPTION_VALUES,
	COMPARE_FORMS,
	DOOR_REASONS,
	COUNTINGS,
	describeElementForCharts,
	describeColumnForCharts,
	describeSystemField,
	bucketAxis,
	kqlFamilyOf,
	sameKqlFamily,
	kqlFamilyLabel,
	isDay,
	rangeMaxDays,
	estimateBuckets,
	refusalFor,
	measureAnswer,
	formsForShape,
	defaultChartFor,
	drawLimit,
	foldFor,
	drawnCount,
	doorFor,
};
